import { supabase } from '@/lib/supabase';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { useAuthStore } from '@/store/authStore';
import { Download, Upload, AlertTriangle, Trash2, Loader2, Database } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import JSZip from 'jszip';

export default function DataSettings() {
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  // Supabase tables to export/import/delete
  const collections = [
    'accounts',
    'transactions',
    'categories',
    'budgets',
    'tags',
    'payees',
    'settings',
    'saving_goals', // Updated to match Supabase table name
    'smart_lists',  // Updated to match Supabase table name
    'folders',
    'rules'
  ];

  const handleExportData = async () => {
    if (!user?.id) {
      console.log('No user ID found');
      toast.error('User not authenticated');
      return;
    }
    
    try {
      console.log('Starting export process...');
      console.log('Current user ID:', user.id);
      setIsLoading(true);
      const zip = new JSZip();
      const exportData: Record<string, any> = {};
      let hasData = false;
      let errorCount = 0;

      // Fetch data from all collections
      console.log('Fetching data from collections...');
      for (const collectionName of collections) {
        try {
          console.log(`Fetching ${collectionName}...`);
          // Get user-specific collection
          const { data, error } = await supabase
            .from(collectionName)
            .select('*')
            .eq('user_id', user.id);
          
          if (error) {
            console.error(`Error fetching ${collectionName}:`, error);
            exportData[collectionName] = [];
            errorCount++;
            continue;
          }

          console.log(`Found ${data.length} records in ${collectionName}`);
          // Log first document for debugging (excluding sensitive data)
          if (data && data.length > 0) {
            const firstDoc = data[0];
            console.log(`Sample document from ${collectionName}:`, {
              id: firstDoc.id,
              hasData: !!firstDoc
            });
          }

          exportData[collectionName] = data || [];
          if (data && data.length > 0) {
            hasData = true;
            console.log(`Found data in ${collectionName}`);
          }
        } catch (collectionError) {
          console.error(`Error fetching ${collectionName}:`, collectionError);
          // Continue with other collections even if one fails
          exportData[collectionName] = [];
          errorCount++;
        }
      }

      if (errorCount > 0 && !hasData) {
        toast.error(`Failed to export data. Encountered ${errorCount} errors.`);
        return;
      }

      // Add metadata
      exportData.metadata = {
        exportDate: new Date().toISOString(),
        userId: user.id,
        version: '1.0',
        appName: 'Flows.Cash',
        collections: collections,
        hasData: hasData,
        totalDocuments: Object.values(exportData)
          .filter(arr => Array.isArray(arr))
          .flat()
          .length
      };

      // Create the JSON file first
      console.log('Creating JSON content...');
      const jsonContent = JSON.stringify(exportData, null, 2);
      console.log('JSON size:', jsonContent.length, 'bytes');
      
      // Add data to zip file
      console.log('Adding data to zip...');
      zip.file('data.json', jsonContent);

      // Generate and download the zip file
      console.log('Generating zip file...');
      const zipBlob = await zip.generateAsync({ 
        type: 'blob',
        compression: 'DEFLATE',
        compressionOptions: { level: 9 }
      });
      console.log('Zip blob created, size:', zipBlob.size, 'bytes');
      
      // Create file name with timestamp
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      const fileName = `flows_cash_backup_${timestamp}.zip`;
      console.log('File name:', fileName);

      // Create download URL
      console.log('Creating download URL...');
      const downloadUrl = window.URL.createObjectURL(zipBlob);
      console.log('Download URL created:', downloadUrl);

      // Create and trigger download
      console.log('Setting up download link...');
      const downloadLink = document.createElement('a');
      downloadLink.style.display = 'none';
      downloadLink.href = downloadUrl;
      downloadLink.download = fileName;
      
      // Append to body and click
      console.log('Triggering download...');
      document.body.appendChild(downloadLink);
      downloadLink.click();
      
      // Cleanup after a short delay
      console.log('Starting cleanup...');
      setTimeout(() => {
        try {
          document.body.removeChild(downloadLink);
          window.URL.revokeObjectURL(downloadUrl);
          console.log('Cleanup completed');
        } catch (cleanupError) {
          console.error('Cleanup error:', cleanupError);
        }
      }, 1000);

      console.log('Export process completed');
      toast.success(hasData ? 'Data exported successfully' : 'Empty backup file created successfully');
    } catch (error) {
      console.error('Export error details:', error);
      toast.error('Failed to export data. Please try again.');
    } finally {
      setIsLoading(false);
      console.log('Export process finished');
    }
  };

  const handleImportData = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!user?.id || !event.target.files?.length) {
      toast.error('Please select a backup file');
      return;
    }

    try {
      setIsLoading(true);
      const file = event.target.files[0];
      
      if (!file.name.endsWith('.zip')) {
        toast.error('Please select a valid backup file (.zip)');
        return;
      }

      console.log('Starting import process...');
      const zip = new JSZip();
      
      // Read zip file
      console.log('Reading backup file...');
      const zipContent = await zip.loadAsync(file);
      const dataFile = await zipContent.file('data.json')?.async('string');
      
      if (!dataFile) {
        toast.error('Invalid backup file format');
        return;
      }

      console.log('Parsing backup data...');
      const importData = JSON.parse(dataFile);
      
      // Validate backup file
      if (!importData.metadata) {
        toast.error('Invalid backup file: missing metadata');
        return;
      }

      console.log('Backup file metadata:', {
        version: importData.metadata.version,
        exportDate: importData.metadata.exportDate,
        appName: importData.metadata.appName || 'Unknown',
        totalDocuments: importData.metadata.totalDocuments
      });

      // Confirmation dialog
      const confirmImport = window.confirm(
        'Importing data will replace your existing data. This action cannot be undone. Do you want to continue?'
      );

      if (!confirmImport) {
        setIsLoading(false);
        return;
      }

      let totalDocs = 0;
      let errorCount = 0;

      // Delete existing data first
      console.log('Deleting existing data...');
      for (const collectionName of collections) {
        try {
          const { error } = await supabase
            .from(collectionName)
            .delete()
            .eq('user_id', user.id);
          
          if (error) {
            console.error(`Error deleting collection ${collectionName}:`, error);
            errorCount++;
          }
        } catch (error) {
          console.error(`Error deleting collection ${collectionName}:`, error);
          errorCount++;
        }
      }

      // Import new data
      console.log('Importing new data...');
      for (const collectionName of collections) {
        const items = importData[collectionName];
        if (!Array.isArray(items) || items.length === 0) {
          console.log(`Skipping ${collectionName}: no valid data`);
          continue;
        }
        
        console.log(`Importing ${items.length} records to ${collectionName}`);

        for (const item of items) {
          try {
            // Remove the id and use the rest of the data
            const { id, ...data } = item;
            // Make sure user_id matches current user
            const itemData = {
              ...data,
              user_id: user.id
            };
            
            const { error } = await supabase
              .from(collectionName)
              .insert([itemData]);
            
            if (error) {
              console.error(`Error inserting to ${collectionName}:`, error);
              errorCount++;
            } else {
              totalDocs++;
            }
          } catch (insertError) {
            console.error(`Error inserting to ${collectionName}:`, insertError);
            errorCount++;
          }
        }
      }

      // Log completion
      console.log(`Imported ${totalDocs} records successfully with ${errorCount} errors`);
      
      if (totalDocs > 0) {
        toast.success(`Data imported successfully (${totalDocs} records)`);
        
        // Reload the page after a short delay to refresh all data
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      } else {
        toast.error('Failed to import data. No records were imported.');
      }
    } catch (error) {
      console.error('Import error details:', error);
      toast.error('Failed to import data. Please try again.');
    } finally {
      setIsLoading(false);
      if (event.target) {
        event.target.value = ''; // Reset file input
      }
    }
  };

  const handleDeleteAccount = async () => {
    if (!user) return;

    try {
      setIsLoading(true);

      // Delete all user data from Supabase tables
      for (const tableName of collections) {
        const { error } = await supabase
          .from(tableName)
          .delete()
          .eq('user_id', user.id);
        
        if (error) {
          console.error(`Error deleting data from ${tableName}:`, error);
        }
      }

      // Delete user's files from storage
      const { error: storageError } = await supabase.storage
        .from('attachments')
        .remove([`users/${user.id}`]);
      
      if (storageError) {
        console.error('Error deleting storage files:', storageError);
      }

      // Delete user account - using the client API instead of admin API
      const { error: deleteError } = await supabase.auth.signOut();
      
      if (deleteError) {
        throw deleteError;
      }
      
      toast.success('Account deleted successfully. Your data has been removed, but you will need to contact support to completely delete your account.');
      navigate('/auth/login');
    } catch (error) {
      console.error('Error deleting account:', error);
      toast.error('Failed to delete account. Please contact support.');
    } finally {
      setIsLoading(false);
      setIsDeleteDialogOpen(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card className="bg-background-secondary/80 dark:bg-background-dark-secondary/80 backdrop-blur-xl backdrop-saturate-150 border border-border dark:border-border-dark shadow-sm">
        <CardHeader>
          <div className="flex items-center gap-2">
            <div className="p-2 rounded-xl bg-primary/10">
              <Database className="h-5 w-5 text-primary dark:text-primary-dark" />
            </div>
            <div>
              <CardTitle className="text-xl font-semibold bg-gradient-to-r from-text-primary to-text-secondary dark:from-text-dark-primary dark:to-text-dark-secondary bg-clip-text text-transparent">
                {t('settings.data.title')}
              </CardTitle>
              <CardDescription className="text-text-secondary dark:text-text-dark-secondary">
                {t('settings.data.description')}
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <Button
              variant="default"
              className="flex items-center gap-2 bg-primary text-white hover:bg-primary/90 dark:bg-primary-dark dark:hover:bg-primary-dark/90 shadow-sm rounded-xl h-11"
              onClick={handleExportData}
              disabled={isLoading}
            >
              {isLoading ? (
                <div className="bg-white/10 p-2 rounded-lg">
                  <Loader2 className="h-4 w-4 animate-spin text-white" />
                </div>
              ) : (
                <Download className="h-4 w-4" />
              )}
              {t('settings.data.exportData')}
            </Button>

            <div className="relative">
              <input
                type="file"
                accept=".zip"
                onChange={handleImportData}
                disabled={isLoading}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer disabled:cursor-not-allowed"
              />
              <Button 
                variant="default" 
                className="flex items-center gap-2 w-full bg-primary text-white hover:bg-primary/90 dark:bg-primary-dark dark:hover:bg-primary-dark/90 shadow-sm rounded-xl h-11"
                disabled={isLoading}
              >
                {isLoading ? (
                  <div className="bg-white/10 p-2 rounded-lg">
                    <Loader2 className="h-4 w-4 animate-spin text-white" />
                  </div>
                ) : (
                  <Upload className="h-4 w-4" />
                )}
                {t('settings.data.importData')}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-background-secondary/80 dark:bg-background-dark-secondary/80 backdrop-blur-xl backdrop-saturate-150 border-2 !border-[#E53935]/50 dark:!border-[#E53935]/50 shadow-sm">
        <CardHeader className="border-b border-[#E53935]/30 dark:border-[#E53935]/30">
          <CardTitle className="text-[#E53935] dark:text-[#E53935] flex items-center gap-2">
            <AlertTriangle className="h-5 w-5" />
            {t('settings.data.dangerZone')}
          </CardTitle>
          <CardDescription className="text-[#E53935]/80 dark:text-[#E53935]/80">
            {t('settings.data.dangerZoneDescription')}
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-6">
          <Alert variant="destructive" className="border-2 !border-[#E53935]/50 !bg-[#E53935]/10 text-[#E53935] shadow-sm">
            <AlertTriangle className="h-5 w-5" />
            <AlertTitle className="text-[#E53935] font-semibold">
              {t('settings.data.deleteAccountTitle')}
            </AlertTitle>
            <AlertDescription className="text-[#E53935]/90">
              {t('settings.data.deleteAccountDescription')}
            </AlertDescription>
          </Alert>

          <Button
            variant="destructive"
            className="mt-6 flex items-center gap-2 !bg-[#E53935] text-white hover:!bg-[#C62828] dark:!bg-[#E53935] dark:text-white dark:hover:!bg-[#C62828] shadow-md border border-[#E53935]/20 rounded-xl h-11 w-full sm:w-auto font-medium"
            onClick={() => setIsDeleteDialogOpen(true)}
            disabled={isLoading}
            style={{ backgroundColor: "#E53935" }}
          >
            {isLoading ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              <Trash2 className="h-5 w-5" />
            )}
            <span className="font-bold">{t('settings.data.deleteAccount')}</span>
          </Button>
        </CardContent>
      </Card>

      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent className="bg-background-secondary/80 dark:bg-background-dark-secondary/80 backdrop-blur-xl backdrop-saturate-150 border-2 !border-[#E53935]/50 dark:!border-[#E53935]/50 rounded-xl">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold text-[#E53935] flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" />
              {t('settings.data.deleteAccountConfirmTitle')}
            </DialogTitle>
            <DialogDescription className="text-[#E53935]/80 dark:text-[#E53935]/80">
              {t('settings.data.deleteAccountConfirmDescription')}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-3 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setIsDeleteDialogOpen(false)}
              className="bg-background-secondary/80 dark:bg-background-dark-secondary/80 text-text-primary dark:text-text-dark-primary hover:bg-background-secondary/90 dark:hover:bg-background-dark-secondary/90 border border-border dark:border-border-dark backdrop-blur-xl backdrop-saturate-150 rounded-xl h-11"
            >
              {t('actions.cancel')}
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteAccount}
              disabled={isLoading}
              className="gap-2 !bg-[#E53935] text-white hover:!bg-[#C62828] dark:!bg-[#E53935] dark:text-white dark:hover:!bg-[#C62828] shadow-md border border-[#E53935]/20 rounded-xl h-11 font-bold"
              style={{ backgroundColor: "#E53935" }}
            >
              {isLoading && <Loader2 className="h-5 w-5 animate-spin" />}
              <span className="font-bold">{t('actions.confirm')}</span>
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
